import {Image} from '@/_application/core/components/ui/image';
import Link from '@/_application/core/components/ui/link';
import {Swiper, SwiperSlide} from '@/_application/core/components/ui/slider';
import Timer from '@/_application/core/components/ui/timer';
import {FlashSaleInfo} from '@/_application/modules/flash-sales/components/home-page/flash-sale-info';
import {ProductCard} from '@/_application/modules/products/components/cards/product-card';
import {getImageAssetUrl} from '@/_application/core/utils/common';
import {Routes} from '@/config/routes';
import {useEffect, useState} from 'react';
import {FlashSale, FlashSaleProduct, FlashSaleProductData} from '@/__generated__/__types__';
import {Product} from '@/_application/modules/products/model/product';

const FlashSaleIcon = getImageAssetUrl('icons/flash-sale.png');

/**
 * Check if flash sale is currently valid based on date range
 */
const checkFlashSaleValid = (flashSaleInfo: FlashSale | null | undefined): boolean => {
    if (!flashSaleInfo) {
        return false;
    }
    const currentDate = new Date();
    const saleStartDate = new Date(flashSaleInfo.date_from!);
    const saleEndDate = new Date(flashSaleInfo.date_to!);
    return currentDate.getTime() >= saleStartDate.getTime() && currentDate.getTime() <= saleEndDate.getTime();
};

export interface FlashSalesSliderMobileProps {
    flashSaleData?: {
        items: FlashSaleProduct[];
        flashSaleInfo: FlashSale | null;
    };
}

export function FlashSalesSliderMobile({flashSaleData}: FlashSalesSliderMobileProps) {
    const [isTimeUp, setIsTimeUp] = useState(false);
    // Only validate on client to avoid hydration mismatch
    const [isValidOnClient, setIsValidOnClient] = useState(true);

    const items = flashSaleData?.items ?? [];
    const flashSaleInfo = flashSaleData?.flashSaleInfo;

    // Validate flash sale only on client side after mount
    useEffect(() => {
        setIsValidOnClient(checkFlashSaleValid(flashSaleInfo));
    }, [flashSaleInfo]);

    const handleTimeUp = () => {
        setIsTimeUp(true);
    };

    // Server: render if has items (let client decide validity)
    // Client: hide if time is up or invalid
    const hasItems = items.length > 0 && flashSaleInfo;

    // Don't render if no data at all
    if (!hasItems) {
        return null;
    }

    // On client, hide if invalid or time is up
    if (typeof window !== 'undefined' && (!isValidOnClient || isTimeUp)) {
        return null;
    }

    return (
        <div className="bg-white pt-6">
            <div className="relative mt-6 bg-gradient-to-tr from-orange-500 to-amber-300 bg-fixed pb-6">
                <div
                    className="absolute -top-5 left-[4%] h-[58px] w-[106px] rounded-xl p-3"
                    style={{background: 'inherit'}}>
                    <Image src={FlashSaleIcon} alt="Flash Sale Icon" height={51} width={102} />
                </div>

                <div className="absolute left-[calc(3%+130px)] top-3">
                    {flashSaleInfo?.date_to && (
                        <Timer
                            dateTo={flashSaleInfo?.date_to}
                            onTimeUp={handleTimeUp}
                            className="items-center gap-1 text-sm font-bold text-white"
                            elementClassName="rounded-[6px] h-5 bg-KPZ_turquoise px-2.5 py-[1px]"
                        />
                    )}
                </div>

                <div className="absolute right-[5%] top-4">
                    <Link href={Routes.flashSale} className="flex items-center gap-1 text-xs font-bold text-white">
                        Xem thêm
                    </Link>
                </div>

                <div className="px-4 pt-12 ">
                    <Swiper slidesPerView={2.2} spaceBetween={8} className={'min-h-[350px]'}>
                        {items.map((flashSaleProduct: FlashSaleProduct, index: number) => (
                            <SwiperSlide key={`${index}-${flashSaleProduct.product?.sku}`} className="mr-4 py-2">
                                <ProductCard
                                    product={flashSaleProduct.product as Product}
                                    additionalData={{
                                        flashSale: flashSaleProduct.sales_data,
                                    }}
                                    afterCardContent={
                                        <FlashSaleInfo data={flashSaleProduct.sales_data as FlashSaleProductData} />
                                    }
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </div>
    );
}
